import type { MastersFilterOptionItem } from "../Apis/mastersFilterOptions";
import { formatChannelLabelForDisplay } from "../utils/displayFormatters";

export type LeadSourceFilterOption = { id: string; label: string };

/**
 * Canonical Source values used on browse-filter fallbacks (CAR-1196 / CAR-2687).
 * VOICE_OC is a valid create Source but is intentionally omitted here — it
 * comes from the masters API for tenants that have Voice OC (CAR-2689).
 */
export const CANONICAL_LEAD_SOURCES = [
  "HIS",
  "Meta",
  "Website",
  "Manual",
  "Hexa",
  "Inbound Call",
  "Patient Care Unit",
] as const;

export type CanonicalLeadSource = (typeof CANONICAL_LEAD_SOURCES)[number];

/** Fallback when /admin/masters/filter-options is unavailable (non-filter surfaces). */
export const LEAD_SOURCE_OPTIONS: LeadSourceFilterOption[] = [
  { id: "HIS", label: "HIS" },
  { id: "Meta", label: "Meta" },
  { id: "Website", label: "Website" },
  { id: "Manual", label: "Manual" },
  { id: "Hexa", label: "Hexa" },
  { id: "Inbound Call", label: "Inbound Call" },
  { id: "Patient Care Unit", label: "Patient Care Unit" },
];

/**
 * Routing-group Source picker fallback when masters cannot be loaded.
 * Includes VOICE_OC (omitted from browse-filter fallbacks by design).
 * Option id is the stored opportunity leadSource (`VOICE_OC`), not the display name.
 */
export const ROUTING_GROUP_LEAD_SOURCE_FALLBACK: LeadSourceFilterOption[] = [
  ...LEAD_SOURCE_OPTIONS,
  { id: "VOICE_OC", label: "Voice OC – WhatsApp Bot" },
];

/** Fallback lead source filter options (My Leads + Overview Opportunities). */
export const LEAD_SOURCE_FILTER_OPTIONS: LeadSourceFilterOption[] = [
  ...LEAD_SOURCE_OPTIONS,
];

/** Source values excluded from My Leads / Overview browse Source filters (API may still return legacy rows). */
const EXCLUDED_LEAD_SOURCE_BROWSE_FILTER_KEYS = new Set([
  "pdc",
  "call centre",
  "call center",
]);

/** Retired Website channels — hide from every Channel dropdown even if masters still return them. Chatbot stays filterable for historical/CMS leads. */
const EXCLUDED_CHANNEL_BROWSE_FILTER_KEYS = new Set([
  "web log",
  "cart lead",
  "order lead",
  "hyperlocal",
  "call back",
]);

function normalizeLeadSourceBrowseFilterKey(id: string): string {
  return id.trim().toLowerCase().replace(/[_-]/g, " ").replace(/\s+/g, " ");
}

function isExcludedLeadSourceBrowseFilterOption(id: string): boolean {
  return EXCLUDED_LEAD_SOURCE_BROWSE_FILTER_KEYS.has(
    normalizeLeadSourceBrowseFilterKey(id)
  );
}

function channelOption(id: string): LeadSourceFilterOption {
  return { id, label: formatChannelLabelForDisplay(id) };
}

/** HIS Channel options (CAR-1197). */
export const HIS_CHANNEL_OPTIONS: LeadSourceFilterOption[] = [
  channelOption("EMR"),
  channelOption("PDC"),
];

/** Website Channel options for create/edit (CMS mx_Enquiry_type values). */
export const WEBSITE_CHANNEL_OPTIONS: LeadSourceFilterOption[] = [
  channelOption("Google Ads"),
  channelOption("Contact_us_dr_appointment"),
  channelOption("Contact_us_phc"),
  channelOption("Contact_us_others"),
  channelOption("website_pop_up_form"),
  channelOption("Abandoned Cart Lead"),
  channelOption("Abandoned Order Lead"),
  channelOption("popup-form"),
  channelOption("appointment-page-lead"),
  channelOption("Hyperlocal Leads"),
  channelOption("general_enquiry"),
  channelOption("corporate_enquiry"),
];

/**
 * Legacy Website Channel values kept in the browse Channel filter only.
 * Chatbot is still stored on historical/CMS leads and must remain filterable.
 * Create/edit uses WEBSITE_CHANNEL_OPTIONS and does not include this list.
 */
export const LEGACY_WEBSITE_CHANNEL_OPTIONS: LeadSourceFilterOption[] = [
  channelOption("Chatbot"),
];

/**
 * Extra Website Channel values the API still accepts on edit (CAR-1197),
 * matching carepro_backend/shared/constants/lead-source-channels.ts.
 * Not offered in the create dropdown.
 */
const BACKEND_WEBSITE_LEGACY_CHANNEL_IDS: readonly string[] = [
  "Web Log",
  "Cart Lead",
  "Order Lead",
  "Hyperlocal",
  "Chatbot",
  "Call Back",
];

/**
 * @deprecated Use LEGACY_WEBSITE_CHANNEL_OPTIONS — kept for existing imports/tests.
 */
export const ADDITIONAL_CHANNEL_FILTER_OPTIONS: LeadSourceFilterOption[] =
  LEGACY_WEBSITE_CHANNEL_OPTIONS;

/** Fallback Channel filter options (sourceChannel). */
export const CHANNEL_OPTIONS: LeadSourceFilterOption[] = [
  ...HIS_CHANNEL_OPTIONS,
  ...WEBSITE_CHANNEL_OPTIONS,
  ...LEGACY_WEBSITE_CHANNEL_OPTIONS,
];

/** @deprecated Use CHANNEL_OPTIONS — legacy name kept for gradual migration. */
export const CAMPAIGN_OPTIONS: LeadSourceFilterOption[] = CHANNEL_OPTIONS;

const CANONICAL_CHANNEL_CODE_KEYS = new Set(
  CHANNEL_OPTIONS.map((opt) => opt.id.trim().toLowerCase())
);

/** Legacy sourceChannel codes that duplicate a canonical Channel value. */
const LEGACY_CHANNEL_DEDUPE_ALIASES: Record<string, string> = {
  his: "emr",
};

function resolveChannelFilterDedupeKey(id: string): string {
  const normalized = id
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ");
  return LEGACY_CHANNEL_DEDUPE_ALIASES[normalized] ?? normalized;
}

function isExcludedChannelBrowseFilterOption(id: string): boolean {
  return EXCLUDED_CHANNEL_BROWSE_FILTER_KEYS.has(
    resolveChannelFilterDedupeKey(id)
  );
}

function isCanonicalChannelCode(id: string): boolean {
  return CANONICAL_CHANNEL_CODE_KEYS.has(id.trim().toLowerCase());
}

function preferChannelFilterOption(
  current: LeadSourceFilterOption,
  candidate: LeadSourceFilterOption
): LeadSourceFilterOption {
  const currentCanonical = isCanonicalChannelCode(current.id);
  const candidateCanonical = isCanonicalChannelCode(candidate.id);
  if (candidateCanonical && !currentCanonical) return candidate;
  return current;
}

function mapMasterItemsToFilterOptions(
  items: MastersFilterOptionItem[] | undefined
): LeadSourceFilterOption[] {
  return (items ?? [])
    .filter((item) => item.isActive !== false)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((item) => ({
      id: item.code,
      label: item.name || item.code,
    }))
    .filter((item) => item.id.trim() !== "");
}

export function mapLeadSourcesToFilterOptions(
  items: MastersFilterOptionItem[] | undefined
): LeadSourceFilterOption[] {
  return mergeLeadSourceFilterOptions(mapMasterItemsToFilterOptions(items));
}

export function mapSourceChannelsToFilterOptions(
  items: MastersFilterOptionItem[] | undefined
): LeadSourceFilterOption[] {
  return mergeChannelFilterOptions(mapMasterItemsToFilterOptions(items));
}

export function mapCampaignsToFilterOptions(
  items: MastersFilterOptionItem[] | undefined
): LeadSourceFilterOption[] {
  return mapMasterItemsToFilterOptions(items);
}

const VOICE_OC_CHANNEL_OPTIONS: LeadSourceFilterOption[] = [
  channelOption("Chatbot"),
];

/**
 * Mirrors backend shared/constants/lead-source-channels.ts (CAR-2687 / CAR-2689 / CAR-2855).
 * HIS/Website stay UI-required (backend still treats them as optional).
 * Manual/Hexa/Inbound Call/Patient Care Unit: optional, full catalog.
 * VOICE_OC: required and fixed to Chatbot.
 * Meta: no Channel (CAR-2855) — omitted here so the field is hidden and never posted.
 */
const CHANNEL_RULES: Record<
  string,
  { options: LeadSourceFilterOption[]; required: boolean }
> = {
  HIS: { options: HIS_CHANNEL_OPTIONS, required: true },
  Website: { options: WEBSITE_CHANNEL_OPTIONS, required: true },
  Manual: { options: CHANNEL_OPTIONS, required: false },
  Hexa: { options: CHANNEL_OPTIONS, required: false },
  "Inbound Call": { options: CHANNEL_OPTIONS, required: false },
  "Patient Care Unit": { options: CHANNEL_OPTIONS, required: false },
  VOICE_OC: { options: VOICE_OC_CHANNEL_OPTIONS, required: true },
};

const MANUAL_CREATE_SOURCE_BY_NORMALIZED_KEY: Record<string, string> = {
  his: "HIS",
  meta: "Meta",
  website: "Website",
  manual: "Manual",
  hexa: "Hexa",
  "inbound call": "Inbound Call",
  "patient care unit": "Patient Care Unit",
  "voice oc": "VOICE_OC",
  voiceoc: "VOICE_OC",
};

/**
 * Map grouping / display labels onto the exact values POST /opportunities and
 * POST /consumer/upsert accept. A tenant grouping named "Voice OC" must send
 * VOICE_OC, not "Voice OC" (backend 400).
 */
export function canonicalizeManualCreateLeadSource(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const normalized = trimmed
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[–—]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (normalized === "voice oc" || normalized.startsWith("voice oc ")) {
    return "VOICE_OC";
  }
  return MANUAL_CREATE_SOURCE_BY_NORMALIZED_KEY[normalized] ?? trimmed;
}

function lookupChannelRule(leadSource: string) {
  const trimmed = leadSource.trim();
  if (!trimmed) return undefined;
  if (Object.prototype.hasOwnProperty.call(CHANNEL_RULES, trimmed)) {
    return CHANNEL_RULES[trimmed];
  }
  const canonical = canonicalizeManualCreateLeadSource(trimmed);
  return Object.prototype.hasOwnProperty.call(CHANNEL_RULES, canonical)
    ? CHANNEL_RULES[canonical]
    : undefined;
}

/** True when Channel is mandatory in the create/edit UI for this Source. */
export function sourceRequiresChannel(leadSource: string): boolean {
  return lookupChannelRule(leadSource)?.required ?? false;
}

/** CAR-2855: Meta is a Source with no Channel (display, forms, and PATCH). */
export function isMetaLeadSource(
  leadSource: string | null | undefined
): boolean {
  if (leadSource == null || typeof leadSource !== "string") return false;
  return canonicalizeManualCreateLeadSource(leadSource) === "Meta";
}

/**
 * Channel value to show for a Source. Meta is always blank even if the API
 * still returns a stored value (GET /opportunities/:id keeps "META" for dedupe).
 */
export function displaySourceChannel(
  leadSource: string | null | undefined,
  sourceChannel: string | null | undefined
): string {
  if (isMetaLeadSource(leadSource)) return "";
  return (sourceChannel ?? "").trim();
}

/**
 * True when the Channel dropdown should be VISIBLE (not hidden) for the
 * given Source. Optional for Manual/Hexa/Inbound Call/Patient Care Unit;
 * required for HIS, Website, and VOICE_OC — see sourceRequiresChannel.
 * Meta never shows Channel (CAR-2855).
 */
export function sourceShowsChannelField(leadSource: string): boolean {
  if (isMetaLeadSource(leadSource)) return false;
  return !!lookupChannelRule(leadSource);
}

/** Channel options for HIS/Website only (empty for every other Source). */
export function getChannelOptionsForSource(
  leadSource: string
): LeadSourceFilterOption[] {
  const normalized = canonicalizeManualCreateLeadSource(leadSource);
  if (normalized === "HIS") return HIS_CHANNEL_OPTIONS;
  if (normalized === "Website") return WEBSITE_CHANNEL_OPTIONS;
  return [];
}

/**
 * Channel options for Add/Edit Opportunity and Add New Contact.
 * HIS/Website keep curated lists; Manual/Hexa/Inbound Call/Patient Care
 * Unit offer the full catalog; VOICE_OC offers Chatbot only; Meta has
 * none (CAR-2855).
 */
export function getOpportunityChannelOptionsForSource(
  leadSource: string
): LeadSourceFilterOption[] {
  return lookupChannelRule(leadSource)?.options ?? [];
}

/**
 * Channels the backend will accept for this Source on save (PATCH included).
 * Wider than the create dropdown for Website: legacy CAR-1197 values stay valid.
 * VOICE_OC is Chatbot only. Empty when the Source is unknown.
 */
export function getBackendAllowedChannelIdsForSource(
  leadSource: string
): readonly string[] {
  const canonical = canonicalizeManualCreateLeadSource(leadSource);
  if (!lookupChannelRule(leadSource)) return [];
  if (canonical === "HIS") {
    return HIS_CHANNEL_OPTIONS.map((opt) => opt.id);
  }
  if (canonical === "Website") {
    return Array.from(
      new Set([
        ...WEBSITE_CHANNEL_OPTIONS.map((opt) => opt.id),
        ...BACKEND_WEBSITE_LEGACY_CHANNEL_IDS,
      ])
    );
  }
  if (canonical === "VOICE_OC") {
    return VOICE_OC_CHANNEL_OPTIONS.map((opt) => opt.id);
  }
  return Array.from(
    new Set([
      ...HIS_CHANNEL_OPTIONS.map((opt) => opt.id),
      ...WEBSITE_CHANNEL_OPTIONS.map((opt) => opt.id),
      ...BACKEND_WEBSITE_LEGACY_CHANNEL_IDS,
    ])
  );
}

/** True when PATCH would accept this Source + Channel pair. */
export function isChannelAllowedForSource(
  leadSource: string,
  channel: string
): boolean {
  const trimmed = channel.trim();
  if (!trimmed) return false;
  return getBackendAllowedChannelIdsForSource(leadSource).includes(trimmed);
}

/**
 * CAR-2690: when a Source has exactly one Channel, that value is the form
 * default. Multi-option Sources start empty so the agent must pick one.
 */
export function getAutoSelectedChannelForSource(leadSource: string): string {
  const options = getOpportunityChannelOptionsForSource(leadSource);
  return options.length === 1 ? options[0].id : "";
}

/** Ensure canonical lead sources are present when API data is sparse. */
export function mergeLeadSourceFilterOptions(
  options: LeadSourceFilterOption[]
): LeadSourceFilterOption[] {
  const map = new Map<string, LeadSourceFilterOption>();
  for (const opt of options) {
    if (isExcludedLeadSourceBrowseFilterOption(opt.id)) continue;
    const key = opt.id.trim().toLowerCase();
    if (key && !map.has(key)) map.set(key, opt);
  }
  for (const fallback of LEAD_SOURCE_FILTER_OPTIONS) {
    const key = fallback.id.trim().toLowerCase();
    if (!map.has(key)) map.set(key, fallback);
  }
  return Array.from(map.values());
}

/** Ensure default Channel options are present alongside API data. */
export function mergeChannelFilterOptions(
  options: LeadSourceFilterOption[]
): LeadSourceFilterOption[] {
  const map = new Map<string, LeadSourceFilterOption>();
  for (const opt of options) {
    if (isExcludedChannelBrowseFilterOption(opt.id)) continue;
    const key = resolveChannelFilterDedupeKey(opt.id);
    if (!key) continue;
    const existing = map.get(key);
    map.set(key, existing ? preferChannelFilterOption(existing, opt) : opt);
  }
  for (const fallback of CHANNEL_OPTIONS) {
    if (isExcludedChannelBrowseFilterOption(fallback.id)) continue;
    const key = resolveChannelFilterDedupeKey(fallback.id);
    if (!key || map.has(key)) continue;
    map.set(key, fallback);
  }
  return Array.from(map.values()).map((opt) => ({
    id: opt.id,
    label: formatChannelLabelForDisplay(opt.label || opt.id),
  }));
}

/** @deprecated Use mergeChannelFilterOptions */
export const mergeCampaignFilterOptions = mergeChannelFilterOptions;

/**
 * Normalizes Channel filter selections to the values stored on opportunities
 * (CAR-1197). Backend filters use exact match on sourceChannel (e.g. "Google Ads",
 * not "GOOGLE_ADS"). Legacy snake codes from older clients are mapped back to
 * canonical labels; CMS mx_Enquiry_type values pass through unchanged.
 */
const CHANNEL_FILTER_VALUE_NORMALIZATION: Record<string, string> = {
  EMR: "EMR",
  PDC: "PDC",
  HIS: "EMR",
  GOOGLE_ADS: "Google Ads",
  WEB_LOG: "Web Log",
  CART_LEAD: "Cart Lead",
  ORDER_LEAD: "Order Lead",
  HYPERLOCAL: "Hyperlocal",
  CHATBOT: "Chatbot",
  CALL_BACK: "Call Back",
};

function resolveChannelFilterValueForApi(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  const explicit = CHANNEL_FILTER_VALUE_NORMALIZATION[trimmed];
  if (explicit) return explicit;

  // HIS codes (EMR, PDC) and canonical Website labels — send as stored
  if (/^[A-Z0-9_]+$/.test(trimmed)) return trimmed;

  // CMS mx_Enquiry_type / vendor strings (mixed case, hyphens) — send as-is
  if (/[a-z-]/.test(trimmed)) return trimmed;

  return trimmed;
}

/** Map Channel filter UI values to stored sourceChannel before API requests. */
export function expandChannelFilterValuesForApi(selected: string[]): string[] {
  const expanded = new Set<string>();
  for (const raw of selected) {
    const resolved = resolveChannelFilterValueForApi(raw);
    if (resolved) expanded.add(resolved);
  }
  return Array.from(expanded);
}

/** @deprecated Use expandChannelFilterValuesForApi */
export const expandCampaignFilterValuesForApi = expandChannelFilterValuesForApi;

/**
 * Create/edit forms must send tenant master codes (e.g. "Manual"), not the
 * filter-catalog ids (e.g. "MANUAL") that mergeLeadSourceFilterOptions injects.
 */
export function buildCreateFormLeadSourceOptions(input: {
  groupingSources: LeadSourceFilterOption[];
  masterSources?: LeadSourceFilterOption[];
}): LeadSourceFilterOption[] {
  const map = new Map<string, LeadSourceFilterOption>();
  const add = (opt: LeadSourceFilterOption) => {
    const id = opt.id.trim();
    if (!id) return;
    const key = id.toLowerCase();
    if (!map.has(key)) {
      map.set(key, { id, label: opt.label.trim() || id });
    }
  };
  const grouping = (input.groupingSources ?? []).filter((opt) => opt.id.trim());
  if (grouping.length > 0) {
    grouping.forEach(add);
    return Array.from(map.values());
  }
  (input.masterSources ?? []).forEach(add);
  return Array.from(map.values());
}

/**
 * Map a selected/prefilled source onto the catalog value the create API expects.
 * "MANUAL" from older records or filter fallbacks becomes "Manual" when that is
 * the option (or the only case-insensitive match). Voice OC display labels
 * resolve to VOICE_OC so the backend enum accepts them.
 */
export function resolveLeadSourceForApi(
  raw: string,
  options: LeadSourceFilterOption[] = []
): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  const exact = options.find((opt) => opt.id === trimmed);
  if (exact) return canonicalizeManualCreateLeadSource(exact.id);

  const lower = trimmed.toLowerCase();
  const caseInsensitive = options.find(
    (opt) => opt.id.toLowerCase() === lower || opt.label.toLowerCase() === lower
  );
  if (caseInsensitive) {
    return canonicalizeManualCreateLeadSource(caseInsensitive.id);
  }

  return canonicalizeManualCreateLeadSource(trimmed);
}

/**
 * Map a prefilled/API Source onto the dropdown option `id` currently in the
 * catalog. Campaign groupings often use display ids like `"Voice OC"` while
 * records/API use `VOICE_OC` — exact match fails and the Source field looks blank.
 * Submit still goes through {@link resolveLeadSourceForApi} for the backend enum.
 */
export function matchLeadSourceCatalogOptionId(
  raw: string,
  options: LeadSourceFilterOption[] = []
): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (options.length === 0) {
    return resolveLeadSourceForApi(trimmed, options);
  }
  if (options.some((opt) => opt.id === trimmed)) return trimmed;

  const wanted = resolveLeadSourceForApi(trimmed, options);
  const match = options.find((opt) => {
    if (opt.id === wanted) return true;
    return resolveLeadSourceForApi(opt.id, []) === wanted;
  });
  return match?.id ?? wanted;
}

function isRoutingGroupMasterOption(
  item: unknown
): item is { code?: string; name?: string; displayOrder?: number } {
  return typeof item === "object" && item !== null;
}

/**
 * Map master rows (`code` / `name`) to routing-group dropdown options.
 * Saved value is always `code` so MatchingCriteria can match opportunity.leadSource.
 */
export function mapMasterCodesToRoutingGroupOptions(
  items: unknown
): LeadSourceFilterOption[] {
  const seen = new Set<string>();
  const mapped: Array<LeadSourceFilterOption & { displayOrder: number }> = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!isRoutingGroupMasterOption(item)) continue;
    const code = typeof item.code === "string" ? item.code.trim() : "";
    if (!code) continue;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const name = typeof item.name === "string" ? item.name.trim() : "";
    mapped.push({
      id: code,
      label: name || code,
      displayOrder:
        typeof item.displayOrder === "number"
          ? item.displayOrder
          : Number.MAX_SAFE_INTEGER,
    });
  }
  mapped.sort((a, b) => a.displayOrder - b.displayOrder);
  return mapped.map(({ id, label }) => ({ id, label }));
}

/**
 * Normalize a stored routing-group lead_source for form display / re-save.
 * Maps legacy uppercase values (WEBSITE, MANUAL, META) to canonical stored casing.
 */
export function normalizeRoutingGroupLeadSourceForForm(
  raw: string,
  catalog: LeadSourceFilterOption[] = ROUTING_GROUP_LEAD_SOURCE_FALLBACK
): string {
  return resolveLeadSourceForApi(raw, catalog);
}

/**
 * Map routing-group Source selections to values stored on opportunities (CAR-1196).
 * Must NOT uppercase Title Case sources like Website, Manual, Meta.
 */
export function mapRoutingGroupLeadSourcesForApi(
  selected: string[],
  catalog: LeadSourceFilterOption[] = ROUTING_GROUP_LEAD_SOURCE_FALLBACK
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of selected ?? []) {
    const resolved = resolveLeadSourceForApi(raw, catalog);
    if (resolved && !seen.has(resolved)) {
      seen.add(resolved);
      result.push(resolved);
    }
  }
  return result;
}
